"use client";

import { useEffect, useMemo, useState } from "react";
import GenericEmptyComponent from "@/shared/ui/GenericEmptyComponent";
import LoadingState from "@/shared/ui/LoadingState";
import { showSuccessToast, showWarningToast } from "@/shared/feedback/toast";
import type { Version } from "@/generated/fastapi";
import { buildSelectedProfile } from "@/shared/utils/selectedProfile";
import {
  useThoughtArrangementPreferences,
  useUserChoices,
  useUserProfile,
} from "@/state/globalStore";
import type {
  ActiveProfileVersion,
  ProfileVersionDrawerMode,
} from "@/state/globalStoreSlices/userChoices";
import useManageVersionState from "../hooks/useManageVersionState";
import useVersionOptions from "../hooks/useVersionOptions";
import useVersionsQuery from "../queries/useVersionsQuery";
import useDeleteVersionsMutation from "../mutations/useDeleteVersionsMutation";
import useApplyVersionSelection from "../hooks/useApplyVersionSelection";
import useCreateVersionMutation from "../mutations/useCreateVersionMutation";
import useExtendVersionMutation from "../mutations/useExtendVersionMutation";
import useUpdateVersionMutation from "../mutations/useUpdateVersionMutation";
import LoadVersionSection from "./manage-version/LoadVersionSection";
import SaveVersionSection from "./manage-version/SaveVersionSection";

type ManageVersionProps = {
  mode: ProfileVersionDrawerMode;
  onClose?: () => void;
};

function toActiveProfileVersion(version: Version): ActiveProfileVersion {
  return {
    id: version.id,
    name: version.name,
    target_id: version.target_id ?? null,
    language: version.language ?? null,
    latest_at: version.latest_at ?? null,
  };
}

function isSameActiveProfileVersion(
  left: ActiveProfileVersion,
  right: ActiveProfileVersion,
) {
  return (
    left.id === right.id &&
    left.name === right.name &&
    left.target_id === right.target_id &&
    left.language === right.language &&
    left.latest_at === right.latest_at
  );
}

export default function ManageVersion({ mode, onClose }: ManageVersionProps) {
  const [versionName, setVersionName] = useState("");
  const [saveTargetVersionId, setSaveTargetVersionId] = useState<
    string | null
  >(null);
  const { userProfile } = useUserProfile();
  const {
    selectedSituations,
    selectedThoughts,
    selectedThoughtsFree,
    selectedSuggestedThoughts,
    targetsToRerank,
    activeStudioGrouping,
    activePrototypeCategory,
    isSelectedProfile,
    countSelectedThoughts,
    activeProfileVersion,
    setActiveProfileVersion,
    clearActiveProfileVersion,
    syncActiveProfileVersion,
  } = useUserChoices();
  const { thoughtArrangementManualOverrides } =
    useThoughtArrangementPreferences();

  const versionsQuery = useVersionsQuery();
  const removeVersions = useDeleteVersionsMutation();
  const saveVersion = useCreateVersionMutation();
  const updateVersion = useUpdateVersionMutation();
  const extendVersion = useExtendVersionMutation();
  const { applyVersionSelection, isReadyToApply } = useApplyVersionSelection();

  const {
    selectedVersions,
    setSelectedVersions,
    selectorEpoch,
    resetSelection,
    canDelete,
    canUseSingleVersion,
  } = useManageVersionState();

  const { versionItems } = useVersionOptions(versionsQuery.data);
  const versions = useMemo(
    () => versionsQuery.data ?? [],
    [versionsQuery.data],
  );
  const selectedVersion = selectedVersions[0];
  const hasManualRerank = useMemo(
    () => Object.values(thoughtArrangementManualOverrides).some(Boolean),
    [thoughtArrangementManualOverrides],
  );
  const canSaveVersion = isSelectedProfile;
  const trimmedVersionName = versionName.trim();
  const canCreateVersion = canSaveVersion && trimmedVersionName.length > 0;
  const canApplyVersion = canUseSingleVersion && isReadyToApply;
  const selectedThoughtCount = countSelectedThoughts();
  const selectedFreeListThoughtCount = selectedThoughtsFree.reduce(
    (count, selection) => count + selection.thoughts_ids.length,
    0,
  );
  const profileFocusName =
    targetsToRerank[0]?.abbr ||
    targetsToRerank[0]?.position ||
    targetsToRerank[0]?.content ||
    null;
  const printLanguage = userProfile.data.print_settings.print_language;
  const isSaveMode = mode === "save_selection";
  const isExtendMode = mode === "extend_selection";
  const activeVersionFromList = useMemo(
    () =>
      activeProfileVersion
        ? (versions.find((version) => version.id === activeProfileVersion.id) ??
          null)
        : null,
    [activeProfileVersion, versions],
  );
  const selectedSaveTargetVersion = useMemo(
    () =>
      saveTargetVersionId
        ? (versions.find((version) => version.id === saveTargetVersionId) ??
          null)
        : activeVersionFromList,
    [activeVersionFromList, saveTargetVersionId, versions],
  );
  const selectedSaveTargetVersions = selectedSaveTargetVersion
    ? [selectedSaveTargetVersion]
    : [];
  const canExtendVersion =
    Boolean(selectedSaveTargetVersion) && canSaveVersion;

  useEffect(() => {
    if (!activeProfileVersion || !versionsQuery.data) {
      return;
    }

    if (versionsQuery.isFetching) {
      return;
    }

    const currentVersion = versionsQuery.data.find(
      (version) => version.id === activeProfileVersion.id,
    );

    if (!currentVersion) {
      clearActiveProfileVersion();
      return;
    }

    const syncedVersion = toActiveProfileVersion(currentVersion);
    if (!isSameActiveProfileVersion(activeProfileVersion, syncedVersion)) {
      syncActiveProfileVersion(syncedVersion);
    }
  }, [
    activeProfileVersion,
    clearActiveProfileVersion,
    syncActiveProfileVersion,
    versionsQuery.data,
    versionsQuery.isFetching,
  ]);

  const buildCurrentSelectedProfile = () =>
    buildSelectedProfile({
      selectedSituations,
      selectedThoughts,
      selectedThoughtsFree,
      includeFreeListOrder: true,
      uiState: {
        activeStudioContent: "confirmed",
        activeStudioGrouping,
        activePrototypeCategory,
        manualRerank: hasManualRerank,
      },
    });

  const handleSetSaveTargetVersions = (versionsToSet: Version[]) => {
    setSaveTargetVersionId(versionsToSet[0]?.id ?? null);
  };

  const handleCreateVersion = async () => {
    if (!canSaveVersion) {
      showWarningToast("Please select a profile before saving it.");
      return;
    }

    if (!trimmedVersionName) {
      showWarningToast("Please provide a valid version name to save.");
      return;
    }

    try {
      const version = await saveVersion.mutateAsync({
        name: trimmedVersionName,
        language: printLanguage,
        target_id: targetsToRerank[0]?.id || null,
        selected_profile: buildCurrentSelectedProfile(),
      });
      setActiveProfileVersion(toActiveProfileVersion(version));
      showSuccessToast("Version saved and set active");
      resetSelection();
      onClose?.();
    } catch {
      showWarningToast("Failed to save version");
    }
  };

  const handleUpdateVersion = async () => {
    if (!selectedSaveTargetVersion || !canSaveVersion) return;

    try {
      const version = await updateVersion.mutateAsync({
        version_id: selectedSaveTargetVersion.id,
        name: selectedSaveTargetVersion.name,
        language: printLanguage,
        target_id: targetsToRerank[0]?.id || null,
        selected_profile: buildCurrentSelectedProfile(),
      });
      setActiveProfileVersion(toActiveProfileVersion(version));
      showSuccessToast("Version updated and set active");
      resetSelection();
      onClose?.();
    } catch {
      showWarningToast("Failed to update version");
    }
  };

  const handleExtendVersion = async () => {
    if (!selectedSaveTargetVersion) {
      showWarningToast("Choose a profile version before extending it.");
      return;
    }

    if (!canExtendVersion) {
      showWarningToast("Select confirmed profile material before extending.");
      return;
    }

    try {
      const version = await extendVersion.mutateAsync({
        version_id: selectedSaveTargetVersion.id,
        language: printLanguage,
        target_id: targetsToRerank[0]?.id || null,
        selected_profile: buildCurrentSelectedProfile(),
      });
      setActiveProfileVersion(toActiveProfileVersion(version));
      showSuccessToast("Version extended and set active");
      resetSelection();
      onClose?.();
    } catch {
      showWarningToast("Failed to extend version");
    }
  };

  const handleLoadVersion = () => {
    if (!canApplyVersion || !selectedVersion) return;

    applyVersionSelection(selectedVersion);
    resetSelection();
    onClose?.();
  };

  const handleDeleteVersion = async () => {
    if (!canDelete) return;

    try {
      await removeVersions.mutateAsync({
        version_ids: selectedVersions.map((v) => v.id),
        delete_mode: "permanent",
      });
      if (
        activeProfileVersion &&
        selectedVersions.some(
          (version) => version.id === activeProfileVersion.id,
        )
      ) {
        clearActiveProfileVersion();
      }
      resetSelection();
      showSuccessToast("Version deleted successfully");
    } catch {
      showWarningToast("Failed to delete version");
    }
  };

  if (versionsQuery.isLoading) {
    return <LoadingState message="Loading versions..." />;
  }

  if (versionsQuery.isError) {
    return <GenericEmptyComponent message="Error loading versions" />;
  }

  if (isSaveMode || isExtendMode) {
    return (
      <SaveVersionSection
        key={mode}
        activeProfileVersion={activeProfileVersion}
        canCreateVersion={canCreateVersion}
        canExtendVersion={canExtendVersion}
        canSaveVersion={canSaveVersion}
        canUseSaveTargetVersion={Boolean(selectedSaveTargetVersion)}
        extendPending={extendVersion.isPending}
        freeListThoughtCount={selectedFreeListThoughtCount}
        hasSelectedSuggestions={selectedSuggestedThoughts.length > 0}
        initialAction={isExtendMode ? "extend" : "create"}
        onClose={onClose}
        onCreateVersion={handleCreateVersion}
        onExtendVersion={handleExtendVersion}
        onUpdateVersion={handleUpdateVersion}
        printLanguage={printLanguage}
        profileFocusName={profileFocusName}
        savePending={saveVersion.isPending}
        selectedSaveTargetVersion={selectedSaveTargetVersion}
        selectedSaveTargetVersions={selectedSaveTargetVersions}
        selectedThoughtCount={selectedThoughtCount}
        selectedSituationCount={selectedSituations.length}
        setSelectedSaveTargetVersions={handleSetSaveTargetVersions}
        selectorEpoch={selectorEpoch}
        updatePending={updateVersion.isPending}
        versionItems={versionItems}
        versionName={versionName}
        setVersionName={setVersionName}
        versions={versions}
      />
    );
  }

  return (
    <LoadVersionSection
      canApplyVersion={canApplyVersion}
      canDelete={canDelete}
      deletePending={removeVersions.isPending}
      onDeleteVersion={handleDeleteVersion}
      onLoadVersion={handleLoadVersion}
      selectedVersion={selectedVersion}
      selectedVersions={selectedVersions}
      setSelectedVersions={setSelectedVersions}
      selectorEpoch={selectorEpoch}
      versionItems={versionItems}
      versions={versions}
    />
  );
}
